// src/components/Admin/Copies/CopiesScreen.jsx
import { useState, useCallback } from "react";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Paper,
  Menu,
  MenuItem as MuiMenuItem,
} from "@mui/material";
import Colors from "../../../assets/Variables/ColorsVariables";
import * as Styles from "../Accounts/Styles.js";
import SearchBoxComponent from "../../Generals/SearchBox/SearchBoxComponent";
import Button3px from "../../Generals/Buttons/3pxButton";
import NotificationModal from "../../Generals/Notifications/NotificationModal";
import TableFrontPagination from "../../Generals/Tables/TableFrontPagination";
import IconComponent from "../../Generals/Icons/IconComponent";
import TitledSelectorSlim from "../../Generals/Selectors/TitledSelectorSlim";
import DateSelector from "../../Generals/Selectors/DateSelector";
import ExportComponent from "../../Generals/ExporterFiles/ExporterFileComponent";
import SummaryReportModal from "../../Generals/Modals/SummaryReportModal";

const CopiesScreen = () => {
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchBy, setSearchBy] = useState("");
  const [dateSelect, setDateSelect] = useState("");
  const [showDatePickers, setShowDatePickers] = useState(false);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [rows, setRows] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);

  // model that determines which detail view to show when clicking on a copy:
  // null | "SignalTicket" | "CopyTicket" | "SignalAccount" | "FollowerAccount"
  const [model, setModel] = useState(null);

  // States for SignalTicket
  const [signalHeader, setSignalHeader] = useState(null);
  const [signalDetails, setSignalDetails] = useState([]);

  // States for CopyTicket
  const [copyHeader, setCopyHeader] = useState(null);
  const [copyDetails, setCopyDetails] = useState([]);

  // States for SignalAccount
  const [signalAccountHeader, setSignalAccountHeader] = useState(null);
  const [signalAccountDetails, setSignalAccountDetails] = useState([]);

  // States for FollowerAccount
  const [followerAccountHeader, setFollowerAccountHeader] = useState(null);
  const [followerAccountDetails, setFollowerAccountDetails] = useState([]);

  // Summary
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [summaryData, setSummaryData] = useState(null);

  const [modalConfig, setModalConfig] = useState({
    open: false,
    iconType: "info",
    title: "",
    message: "",
    onAcceptLabel: "OK",
    onRefuseLabel: undefined,
    onContinueLabel: undefined,
    onAccept: undefined,
    onRefuse: undefined,
    onContinue: undefined,
    onClose: () => setModalConfig((prev) => ({ ...prev, open: false })),
  });

  // =====================
  // Constants
  // =====================

  // Search by
  const SEARCH_BY_OPTIONS = [
    { label: "Select an option", value: "" },
    { label: "Account", value: "ACCOUNT" },
    { label: "Ticket", value: "TICKET" },
  ];

  // Period of time
  const DATE_OPTIONS = [
    { label: "Today", value: "Today" },
    { label: "Last Week", value: "LastWeek" },
    { label: "Last Month", value: "LastMonth" },
    { label: "Last Year", value: "LastYear" },
    { label: "Custom date", value: "Custom" },
  ];

  // Columns for the basic view (Generic Copies)
  const COPIES_COLUMNS = [
    { field: "id", headerName: "ID", flex: 0.4 },
    { field: "login", headerName: "Login", flex: 1 },
    { field: "ticket", headerName: "Ticket", flex: 1 },
    { field: "symbol", headerName: "Symbol", flex: 1 },
    { field: "type", headerName: "Type", flex: 0.8 },
    { field: "volume", headerName: "Volume", flex: 0.8 },
    { field: "openTime", headerName: "Open time", flex: 1.2 },
    { field: "status", headerName: "Status", flex: 0.8 },
  ];

  // Data MOCK for the basic view (Generic Copies)
  const MOCK_COPIES_DATA = [
    {
      id: 1,
      login: "12345678",
      ticket: "#1001",
      symbol: "EURUSD",
      type: "BUY",
      volume: "0.10",
      openTime: "2026.01.23 02:15:30",
      status: "OPEN",
    },
    {
      id: 2,
      login: "87654321",
      ticket: "#1002",
      symbol: "GBPUSD",
      type: "SELL",
      volume: "0.25",
      openTime: "2026.01.23 02:14:12",
      status: "OPEN",
    },
    {
      id: 3,
      login: "11223344",
      ticket: "#1003",
      symbol: "XAUUSD",
      type: "BUY",
      volume: "0.05",
      openTime: "2026.01.23 02:10:45",
      status: "CLOSED",
    },
  ];

  // MOCK for Summary Report
  const MOCK_SUMMARY_DATA = {
    signal: "123456",
    ticket: "123456",
    totalFollowers: 100,
    ignoredCopies: 10,
    copiedTickets: 80,
    errors: [
      {
        follower: "123456",
        ticket: "123456",
        reason: "Out of Money",
      },
      {
        follower: "223344",
        ticket: "123457",
        reason: "Volume error",
      },
      {
        follower: "334455",
        ticket: "123458",
        reason: "Out of Money",
      },
    ],
  };

  // =====================
  // MODEL SIGNAL TICKET (MOCK)
  // =====================

  const SIGNAL_DETAIL_COLUMNS = [
    { field: "follower", headerName: "Follower", flex: 1 },
    { field: "copyTicket", headerName: "CopyTicket", flex: 1 },
    { field: "type", headerName: "Type", flex: 0.8 },
    { field: "symbol", headerName: "Symbol", flex: 1 },
    { field: "volume", headerName: "Volume", flex: 0.8 },
    { field: "openTime", headerName: "Open time", flex: 1.2 },
    { field: "openPrice", headerName: "Open Price", flex: 1 },
    { field: "closeTime", headerName: "Close time", flex: 1.2 },
    { field: "closePrice", headerName: "Close Price", flex: 1 },
    { field: "commission", headerName: "Commission", flex: 0.9 },
    { field: "swap", headerName: "Swap", flex: 0.8 },
    { field: "profit", headerName: "Profit", flex: 0.8 },
    { field: "comment", headerName: "Comment", flex: 1 },
  ];

  const MOCK_SIGNAL_MODEL = {
    model: "SignalTicket",
    signalTicket: "#123456",
    signalLogin: "123456",
    header: {
      signal: "123456",
      type: "ILC",
      markup: "0.001",
      ticket: "123456",
      typeOrder: "BUY",
      volume: "0.5",
      symbol: "EURUSD",
      openTime: "17/05/2025 15:04:01",
      openPrice: "1.3456",
      closeTime: "17/05/2025 15:04:01",
      closePrice: "1.3456",
      commission: "5.4",
      swap: "1.2",
      profit: "123456",
      currency: "USD",
    },
    details: [
      {
        id: 1,
        follower: "123456",
        copyTicket: "123456",
        type: "Buy",
        symbol: "EURUSD",
        volume: "1.5",
        openTime: "17/05/2025 15:04:01",
        openPrice: "1.23456",
        closeTime: "17/05/2025 15:04:01",
        closePrice: "1.23456",
        commission: "45",
        swap: "45",
        profit: "45",
        comment: "test",
      },
      {
        id: 2,
        follower: "123456",
        copyTicket: "123456",
        type: "Sell",
        symbol: "EURUSD",
        volume: "1.5",
        openTime: "17/05/2025 15:04:01",
        openPrice: "1.23456",
        closeTime: "17/05/2025 15:04:01",
        closePrice: "1.23456",
        commission: "45",
        swap: "45",
        profit: "-45",
        comment: "test",
      },
    ],
  };

  // =====================
  // MODEL COPY TICKET (MOCK)
  // =====================

  const COPY_DETAIL_COLUMNS = [
    { field: "signal", headerName: "Signal", flex: 1 },
    { field: "signalTicket", headerName: "SignalTicket", flex: 1 },
    { field: "type", headerName: "Type", flex: 0.8 },
    { field: "symbol", headerName: "Symbol", flex: 1 },
    { field: "volume", headerName: "Volume", flex: 0.8 },
    { field: "openTime", headerName: "Open time", flex: 1.2 },
    { field: "openPrice", headerName: "Open Price", flex: 1 },
    { field: "closeTime", headerName: "Close time", flex: 1.2 },
    { field: "closePrice", headerName: "Close Price", flex: 1 },
    { field: "commission", headerName: "Commission", flex: 0.9 },
    { field: "swap", headerName: "Swap", flex: 0.8 },
    { field: "profit", headerName: "Profit", flex: 0.8 },
    { field: "comment", headerName: "Comment", flex: 1 },
    {
      field: "copies",
      headerName: "Copies",
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <Button
          variant="outlined"
          sx={Styles.TicketButton}
          onClick={() => handleGoToSignalTicket(row.signalTicket, row.signal)}
        >
          <IconComponent icon="receipt" size="20px" />
        </Button>
      ),
    },
  ];

  const MOCK_COPY_MODEL = {
    model: "CopyTicket",
    copyTicket: "#123456",
    follower: "123456",
    signalTicket: "#12345",
    signalLogin: "123456",
    header: {
      copyTicket: "#123456",
      follower: "123456",
      signalTicket: "#12345",
      signal: "123456",
      typeOrder: "BUY",
      volume: "0.5",
      symbol: "EURUSD",
      openTime: "17/05/2025 15:04:01",
      openPrice: "1.3456",
      closeTime: "17/05/2025 15:04:01",
      closePrice: "1.3456",
      commission: "5.4",
      swap: "1.2",
      profit: "123456",
      currency: "USD",
    },
    details: [
      {
        id: 1,
        signal: "123456",
        signalTicket: "#12345",
        type: "Buy",
        symbol: "EURUSD",
        volume: "1.5",
        openTime: "17/05/2025 15:04:01",
        openPrice: "1.23456",
        closeTime: "17/05/2025 15:04:01",
        closePrice: "1.23456",
        commission: "45",
        swap: "45",
        profit: "45",
        comment: "comment test :)",
      },
    ],
  };

  // =====================
  // MODEL SIGNAL ACCOUNT (MOCK)
  // =====================

  // Card superior SignalAccount
  const SIGNAL_ACCOUNT_HEADER_FIELDS = [
    "signalLogin",
    "strategy",
    "type",
    "market",
    "markup",
    "margin",
    "freeMargin",
    "balance",
    "credit",
    "equity",
    "pnl",
    "currency",
    "group",
  ];

  // Table inferior SignalAccount (SignalTicket with date range)
  const SIGNAL_ACCOUNT_DETAIL_COLUMNS = [
    { field: "signalTicket", headerName: "SignalTicket", flex: 1 },
    { field: "type", headerName: "Type", flex: 0.8 },
    { field: "symbol", headerName: "Symbol", flex: 1 },
    { field: "volume", headerName: "Volume", flex: 0.8 },
    { field: "openTime", headerName: "Open time", flex: 1.2 },
    { field: "openPrice", headerName: "Open Price", flex: 1 },
    { field: "closeTime", headerName: "Close time", flex: 1.2 },
    { field: "closePrice", headerName: "Close Price", flex: 1 },
    { field: "commission", headerName: "Commission", flex: 0.9 },
    { field: "swap", headerName: "Swap", flex: 0.8 },
    { field: "profit", headerName: "Profit", flex: 0.8 },
    { field: "comment", headerName: "Comment", flex: 1 },
    {
      field: "copies",
      headerName: "Copies",
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <Button
          variant="outlined"
          sx={Styles.TicketButton}
          // Clicking on it takes you to the SignalTicket details for that SignalTicket
          onClick={() => handleGoToSignalTicket(row.signalTicket, signalAccountHeader?.signalLogin)}
        >
          <IconComponent icon="receipt" size="20px" />
        </Button>
      ),
    },
  ];

  const MOCK_SIGNAL_ACCOUNT_MODEL = {
    model: "SignalAccount",
    signalLogin: "123456",
    header: {
      signalLogin: "123456",
      strategy: "Strategy Test",
      type: "ILC",
      market: "Forex",
      markup: "0.001",
      margin: "123",
      freeMargin: "78965",
      balance: "1100",
      credit: "100",
      equity: "200",
      pnl: "1100",
      currency: "USD",
      group: "demoforex-USD",
    },
    details: [
      {
        id: 1,
        signalTicket: "123456",
        type: "Buy",
        symbol: "EURUSD",
        volume: "1.5",
        openTime: "17/05/2025 15:04:01",
        openPrice: "1.23456",
        closeTime: "17/05/2025 15:04:01",
        closePrice: "1.23456",
        commission: "45",
        swap: "45",
        profit: "45",
        comment: "comment test :)",
      },
      {
        id: 2,
        signalTicket: "123457",
        type: "Sell",
        symbol: "EURUSD",
        volume: "1.5",
        openTime: "17/05/2025 15:04:01",
        openPrice: "1.23456",
        closeTime: "17/05/2025 15:04:01",
        closePrice: "1.23456",
        commission: "45",
        swap: "45",
        profit: "-45",
        comment: "Si ves esto me debes plata",
      },
    ],
  };

  // =====================
  // MODEL FOLLOWER ACCOUNT (MOCK)
  // =====================

  // Card superior FollowerAccount
  const FOLLOWER_ACCOUNT_HEADER_FIELDS = [
    "followerLogin",
    "signal",
    "type",
    "market",
    "markup",
    "margin",
    "freeMargin",
    "balance",
    "credit",
    "equity",
    "pnl",
    "currency",
    "group",
  ];

  // Table inferior FollowerAccount (CopyTickets of this follower)
  const FOLLOWER_ACCOUNT_DETAIL_COLUMNS = [
    { field: "copyTicket", headerName: "CopyTicket", flex: 1 },
    { field: "ticket", headerName: "Ticket", flex: 1 },
    { field: "type", headerName: "Type", flex: 0.8 },
    { field: "symbol", headerName: "Symbol", flex: 1 },
    { field: "volume", headerName: "Volume", flex: 0.8 },
    { field: "openTime", headerName: "Open time", flex: 1.2 },
    { field: "openPrice", headerName: "Open Price", flex: 1 },
    { field: "closeTime", headerName: "Close time", flex: 1.2 },
    { field: "closePrice", headerName: "Close Price", flex: 1 },
    { field: "commission", headerName: "Commission", flex: 0.9 },
    { field: "swap", headerName: "Swap", flex: 0.8 },
    { field: "profit", headerName: "Profit", flex: 0.8 },
    { field: "comment", headerName: "Comment", flex: 1 },
    {
      field: "copies",
      headerName: "Copies",
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <Button
          variant="outlined"
          sx={Styles.TicketButton}
          // Clicking on it takes you to the CopyTicket details for that CopyTicket
          onClick={() => handleGoToCopyTicket(row.copyTicket, followerAccountHeader?.followerLogin)}
        >
          <IconComponent icon="receipt" size="20px" />
        </Button>
      ),
    },
  ];

  const MOCK_FOLLOWER_ACCOUNT_MODEL = {
    model: "FollowerAccount",
    followerLogin: "654321",
    header: {
      followerLogin: "654321",
      signal: "123456",
      type: "iLC",
      market: "Forex",
      markup: "0.001",
      margin: "123",
      freeMargin: "78965",
      balance: "1100",
      credit: "100",
      equity: "200",
      pnl: "1100",
      currency: "USD",
      group: "demoforex-USD",
    },
    details: [
      {
        id: 1,
        copyTicket: "COPY-123456",
        ticket: "123456",
        type: "Buy",
        symbol: "EURUSD",
        volume: "1.5",
        openTime: "17/05/2025 15:04:01",
        openPrice: "1.23456",
        closeTime: "17/05/2025 15:04:01",
        closePrice: "1.23456",
        commission: "45",
        swap: "45",
        profit: "45",
        comment: "comment test :)",
      },
      {
        id: 2,
        copyTicket: "COPY-123457",
        ticket: "123457",
        type: "Sell",
        symbol: "EURUSD",
        volume: "1.5",
        openTime: "17/05/2025 15:04:01",
        openPrice: "1.23456",
        closeTime: "17/05/2025 15:04:01",
        closePrice: "1.23456",
        commission: "45",
        swap: "45",
        profit: "-45",
        comment: "buuuu :o",
      },
    ],
  };

  // =====================
  // Helpers dates / filters
  // =====================

  const toUnix = (date) => (date ? Math.floor(date.getTime() / 1000) : null);

  const buildFilters = () => {
    let from = null;
    let to = null;
    const now = new Date();

    switch (dateSelect) {
      case "Today": {
        const start = new Date();
        start.setHours(0, 0, 0, 0);
        const end = new Date();
        end.setHours(23, 59, 59, 999);
        from = toUnix(start);
        to = toUnix(end);
        break;
      }
      case "LastWeek": {
        const end = new Date();
        end.setHours(23, 59, 59, 999);
        const start = new Date();
        start.setDate(end.getDate() - 7);
        start.setHours(0, 0, 0, 0);
        from = toUnix(start);
        to = toUnix(end);
        break;
      }
      case "LastMonth": {
        const start = new Date(
          now.getFullYear(),
          now.getMonth() - 1,
          1,
          0,
          0,
          0,
          0
        );
        const end = new Date(
          now.getFullYear(),
          now.getMonth(),
          0,
          23,
          59,
          59,
          999
        );
        from = toUnix(start);
        to = toUnix(end);
        break;
      }
      case "LastYear": {
        const start = new Date(now.getFullYear() - 1, 0, 1, 0, 0, 0, 0);
        const end = new Date(
          now.getFullYear() - 1,
          11,
          31,
          23,
          59,
          59,
          999
        );
        from = toUnix(start);
        to = toUnix(end);
        break;
      }
      case "Custom": {
        from = toUnix(fromDate);
        to = toUnix(toDate);
        break;
      }
      default:
        break;
    }

    return {
      search: searchTerm || null,
      searchBy: searchBy || null,
      from,
      to,
    };
  };

  // =====================
  // Export
  // =====================

  const handleExportClick = (event) => setAnchorEl(event.currentTarget);
  const handleExportClose = () => setAnchorEl(null);

  const handleExport = (type) => {
    const today = new Date();
    const formattedDate = today.toISOString().slice(0, 10).replace(/-/g, "");

    // select data and columns based on the current model
    const dataToExport =
      model === "SignalTicket"
        ? signalDetails
        : model === "CopyTicket"
        ? copyDetails
        : model === "SignalAccount"
        ? signalAccountDetails
        : model === "FollowerAccount"
        ? followerAccountDetails
        : rows;

    const columnsToExport =
      model === "SignalTicket"
        ? SIGNAL_DETAIL_COLUMNS
        : model === "CopyTicket"
        ? COPY_DETAIL_COLUMNS
        : model === "SignalAccount"
        ? SIGNAL_ACCOUNT_DETAIL_COLUMNS
        : model === "FollowerAccount"
        ? FOLLOWER_ACCOUNT_DETAIL_COLUMNS
        : COPIES_COLUMNS;

    const exportColumns = columnsToExport.filter((c) => c.field !== "actions");

    if (type === "csv") {
      ExportComponent.GenerateCSV(
        exportColumns,
        dataToExport,
        `copies_${formattedDate}`
      );
    }
    if (type === "pdf") {
      ExportComponent.GeneratePDF(
        exportColumns,
        dataToExport,
        `copies_${formattedDate}`
      );
    }
    if (type === "txt") {
      ExportComponent.GenerateTXT(
        exportColumns,
        dataToExport,
        `copies_${formattedDate}`
      );
    }

    handleExportClose();
  };

  // =====================
  // Request al backend (comented)
  // =====================

  const requestCopies = async () => {
    /*
    try {
      setLoading(true);
      const filters = buildFilters();

      const params = {
        search: filters.search ?? undefined,
        searchBy: filters.searchBy ?? undefined,
        from: filters.from ?? undefined,
        to: filters.to ?? undefined,
      };

      const res = await copiesMethods.getCopies(params);
      const { status, data, message } = res.data || {};

      if (status !== "OK" && status !== "Success" && status !== 200) {
        setModalConfig((prev) => ({
          ...prev,
          open: true,
          iconType: "error",
          title: "Error loading copies",
          message: message || "Unexpected error while fetching copies.",
        }));
        cleanAllModels();
        return;
      }

      // aquí se decide según data.model
      switch (data?.model) {
        case "SignalTicket":
          setModel("SignalTicket");
          setSignalHeader(data.header);
          setSignalDetails(
            (data.details || []).map((item, index) => ({
              id: index + 1,
              ...item,
            }))
          );
          resetCopyTicket();
          resetSignalAccount();
          resetFollowerAccount();
          setRows([]);
          break;

        case "CopyTicket":
          setModel("CopyTicket");
          setCopyHeader(data.header);
          setCopyDetails(
            (data.details || []).map((item, index) => ({
              id: index + 1,
              ...item,
            }))
          );
          resetSignalTicket();
          resetSignalAccount();
          resetFollowerAccount();
          setRows([]);
          break;

        case "SignalAccount":
          setModel("SignalAccount");
          setSignalAccountHeader(data.header);
          setSignalAccountDetails(
            (data.details || []).map((item, index) => ({
              id: index + 1,
              ...item,
            }))
          );
          resetSignalTicket();
          resetCopyTicket();
          resetFollowerAccount();
          setRows([]);
          break;

        case "FollowerAccount":
          setModel("FollowerAccount");
          setFollowerAccountHeader(data.header);
          setFollowerAccountDetails(
            (data.details || []).map((item, index) => ({
              id: index + 1,
              ...item,
            }))
          );
          resetSignalTicket();
          resetCopyTicket();
          resetSignalAccount();
          setRows([]);
          break;

        default:
          // modelo básico de listado
          setModel(null);
          resetSignalTicket();
          resetCopyTicket();
          resetSignalAccount();
          resetFollowerAccount();
          setRows((data.items || []).map((item, index) => ({
            id: index + 1,
            ...item,
          })));
          break;
      }
    } catch (error) {
      console.error("Error fetching copies:", error);
      const backendMessage =
        error?.response?.data?.message ||
        "There was an error contacting the copies service.";
      setModalConfig((prev) => ({
        ...prev,
        open: true,
        iconType: "error",
        title: "Server error",
        message: backendMessage,
      }));
      cleanAllModels();
    } finally {
      setLoading(false);
    }
    */
  };

  // =====================
  // Helpers for resetting models
  // =====================

  const resetSignalTicket = () => {
    setSignalHeader(null);
    setSignalDetails([]);
  };

  const resetCopyTicket = () => {
    setCopyHeader(null);
    setCopyDetails([]);
  };

  const resetSignalAccount = () => {
    setSignalAccountHeader(null);
    setSignalAccountDetails([]);
  };

  const resetFollowerAccount = () => {
    setFollowerAccountHeader(null);
    setFollowerAccountDetails([]);
  };

  const cleanAllModels = () => {
    setModel(null);
    resetSignalTicket();
    resetCopyTicket();
    resetSignalAccount();
    resetFollowerAccount();
    setRows([]);
  };

  // =====================
  // Function MOCK for simulating search and response from backend
  // =====================

  const simulateSearch = () => {
    setLoading(true);
    setTimeout(() => {
      const filters = buildFilters();

      // Lógica de prueba:
      // - SearchBy = TICKET:
      //     * search starts with "copy" => CopyTicket
      //     * any other value => SignalTicket
      // - SearchBy = ACCOUNT:
      //     * search starts with "sig" => SignalAccount
      //     * search starts with "fol" => FollowerAccount
      //     * in any other case => basic table
      if (searchBy === "TICKET" && filters.search) {
        if (filters.search.toLowerCase().startsWith("copy")) {
          setModel("CopyTicket");
          setCopyHeader(MOCK_COPY_MODEL.header);
          setCopyDetails(MOCK_COPY_MODEL.details);
          resetSignalTicket();
          resetSignalAccount();
          resetFollowerAccount();
          setRows([]);
        } else {
          setModel("SignalTicket");
          setSignalHeader(MOCK_SIGNAL_MODEL.header);
          setSignalDetails(MOCK_SIGNAL_MODEL.details);
          resetCopyTicket();
          resetSignalAccount();
          resetFollowerAccount();
          setRows([]);
        }
      } else if (searchBy === "ACCOUNT" && filters.search) {
        const searchLower = filters.search.toLowerCase();
        if (searchLower.startsWith("sig")) {
          // SignalAccount
          setModel("SignalAccount");
          setSignalAccountHeader(MOCK_SIGNAL_ACCOUNT_MODEL.header);
          setSignalAccountDetails(MOCK_SIGNAL_ACCOUNT_MODEL.details);
          resetSignalTicket();
          resetCopyTicket();
          resetFollowerAccount();
          setRows([]);
        } else if (searchLower.startsWith("fol")) {
          // FollowerAccount
          setModel("FollowerAccount");
          setFollowerAccountHeader(MOCK_FOLLOWER_ACCOUNT_MODEL.header);
          setFollowerAccountDetails(MOCK_FOLLOWER_ACCOUNT_MODEL.details);
          resetSignalTicket();
          resetCopyTicket();
          resetSignalAccount();
          setRows([]);
        } else {
          // any other account login => basic view
          setModel(null);
          resetSignalTicket();
          resetCopyTicket();
          resetSignalAccount();
          resetFollowerAccount();
          setRows(MOCK_COPIES_DATA);
        }
      } else {
        // without filters or invalid filters => basic table
        setModel(null);
        resetSignalTicket();
        resetCopyTicket();
        resetSignalAccount();
        resetFollowerAccount();
        setRows(MOCK_COPIES_DATA);
      }

      setLoading(false);
    }, 800);
  };

  // =====================
  // Handlers UI
  // =====================

  const handleClearFilters = () => {
    setSearchTerm("");
    setSearchBy("");
    setDateSelect("");
    setShowDatePickers(false);
    setFromDate(null);
    setToDate(null);
    cleanAllModels();
  };

  // Handler for navigating to SignalTicket details when clicking from CopyTicket or FollowerAccount
  const handleGoToSignalTicket = useCallback((signalTicket, signalLogin) => {
    // Simulates direct search of the SignalTicket
    setSearchTerm(signalTicket);
    setSearchBy("TICKET");
    setDateSelect(""); // reset date filter to show all results for that ticket

    // In production, you would call requestCopies() here to fetch the data for that specific SignalTicket
    setTimeout(() => {
      setModel("SignalTicket");
      setSignalHeader(MOCK_SIGNAL_MODEL.header);
      setSignalDetails(MOCK_SIGNAL_MODEL.details);
      resetCopyTicket();
      resetSignalAccount();
      resetFollowerAccount();
      setRows([]);
    }, 300);
  }, []);

  // Handler for navigating to CopyTicket details when clicking from FollowerAccount
  const handleGoToCopyTicket = useCallback((copyTicket, followerLogin) => {
    // Simulates direct search of the CopyTicket
    setSearchTerm(copyTicket);
    setSearchBy("TICKET");
    setDateSelect("");

    // In production, you would call requestCopies() here to fetch the data for that specific CopyTicket
    setTimeout(() => {
      setModel("CopyTicket");
      setCopyHeader(MOCK_COPY_MODEL.header);
      setCopyDetails(MOCK_COPY_MODEL.details);
      resetSignalTicket();
      resetSignalAccount();
      resetFollowerAccount();
      setRows([]);
    }, 300);
  }, []);

  const handleDateSelector = (event) => {
    const value = event.target.value;
    setDateSelect(value);
    if (value === "Custom") {
      setShowDatePickers(true);
    } else {
      setShowDatePickers(false);
    }
  };

  const handleSearchByChange = (event) => {
    const value = event.target.value;
    setSearchBy(value);

    // the date range only applies when searching by Ticket (SignalTicket / CopyTicket / Accounts by ticket)
    if (value !== "TICKET") {
      setDateSelect("");
      setShowDatePickers(false);
      setFromDate(null);
      setToDate(null);
    }
  };

  const handleSearchClick = () => {
    simulateSearch();
    // requestCopies(); // when connecting to backend, replace simulateSearch with requestCopies
  };

  const isPeriodDisabled = searchBy !== "TICKET";

  // =====================
  // Render
  // =====================

  return (
    <Paper elevation={2} sx={Styles.containerPaper}>
      <NotificationModal {...modalConfig} />

      {/* Summary Report Modal */}
      <SummaryReportModal
        open={summaryOpen}
        onClose={() => setSummaryOpen(false)}
        data={summaryData}
      />

      <Typography variant="h5" gutterBottom>
        Copies
      </Typography>
      <Typography sx={{ fontSize: "15px", color: "#687B8E", mb: 3 }}>
        Trading copy review and monitoring module.
      </Typography>

      {/* Filters bar */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 2,
          mb: 2,
          p: 2,
          bgcolor: "#F8FAFC",
          borderRadius: "10px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
        }}
      >
        {/* Search */}
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography
            sx={{
              fontFamily: "Plus Jakarta Sans, sans-serif",
              fontWeight: 400,
              fontSize: "12px",
              lineHeight: 2.5,
              color: Colors.Secondary_700,
              marginTop: "-10px",
            }}
          >
            Search
          </Typography>
          <SearchBoxComponent
            value={searchTerm}
            handleSearchTerm={(e) => setSearchTerm(e.target.value)}
            placeholderprops={"Search by login or ticket"}
            variant="statements"
          />
        </Box>

        {/* Search by */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <Typography
            sx={{
              fontFamily: "Plus Jakarta Sans, sans-serif",
              fontWeight: 400,
              fontSize: "11px",
              lineHeight: "140%",
              color: Colors.Secondary_700,
            }}
          >
            Search by
          </Typography>
          <TitledSelectorSlim
            title=""
            value={searchBy}
            handleChange={handleSearchByChange}
            options={SEARCH_BY_OPTIONS}
            variant="statements"
          />
        </Box>

        {/* Period of time */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <Typography
            sx={{
              fontFamily: "Plus Jakarta Sans, sans-serif",
              fontWeight: 400,
              fontSize: "11px",
              lineHeight: "140%",
              color: Colors.Secondary_700,
            }}
          >
            Period of time
          </Typography>
          <TitledSelectorSlim
            title=""
            value={dateSelect}
            handleChange={handleDateSelector}
            options={DATE_OPTIONS}
            variant="statements"
            disabled={isPeriodDisabled}
          />
        </Box>

        {/* Date pickers cuando es Custom y Search by = Ticket */}
        {searchBy === "TICKET" && showDatePickers && (
          <>
            <DateSelector
              title="From"
              value={fromDate}
              handleChange={setFromDate}
              variant="statements"
            />
            <DateSelector
              title="To"
              value={toDate}
              handleChange={setToDate}
              variant="statements"
            />
          </>
        )}

        {/* Buttons Clear / Search */}
        <Box
          sx={{
            marginTop: "17px",
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            ml: { xs: 0, md: 0 },
          }}
        >
          <Button
            variant="outlined"
            sx={{
              minWidth: 40,
              padding: "12px 10px",
              borderRadius: "8px",
              borderColor: Colors.Secondary_50,
              color: Colors.Gray_600,
              "&:hover": {
                borderColor: Colors.Secondary_100,
                backgroundColor: Colors.Gray_50,
              },
            }}
            onClick={handleClearFilters}
          >
            <IconComponent icon="loop" size="16px" />
            <Typography
              sx={{
                fontFamily: "Plus Jakarta Sans, sans-serif",
                fontWeight: 700,
                fontSize: "12px",
                lineHeight: 1.4,
                textAlign: "center",
                color: Colors.Gray_600,
              }}
            >
              Clear
            </Typography>
          </Button>

          <Button3px
            label={"Search"}
            color={Colors.Warning_50}
            backgroundColor={Colors.Error_500}
            type="submit"
            HandleOnClick={handleSearchClick}
            height="40px"
            disabled={
              searchBy === "TICKET" &&
              dateSelect === "Custom" &&
              (!fromDate || !toDate)
            }
          >
            Search
          </Button3px>
        </Box>

        {/* Export aligned to the right */}
        <Box
          sx={{
            marginTop: "17px",
            marginLeft: "auto",
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <Button
            variant="outlined"
            sx={Styles.ExportButton}
            onClick={handleExportClick}
          >
            <IconComponent icon="download" size="16px" />
            <Typography
              sx={{
                fontFamily: "Plus Jakarta Sans, sans-serif",
                fontWeight: 700,
                fontSize: "12px",
                lineHeight: 1.4,
                textAlign: "center",
              }}
            >
              Export
            </Typography>
          </Button>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleExportClose}
          >
            <MuiMenuItem onClick={() => handleExport("csv")}>
              Export as CSV
            </MuiMenuItem>
            <MuiMenuItem onClick={() => handleExport("pdf")}>
              Export as PDF
            </MuiMenuItem>
            <MuiMenuItem onClick={() => handleExport("txt")}>
              Export as TXT
            </MuiMenuItem>
          </Menu>
        </Box>
      </Box>

      {/* Subtitle based on model */}
      {model === "SignalTicket" && (
        <Typography
          sx={{
            mb: 2,
            fontSize: "14px",
            fontWeight: 600,
            color: Colors.Secondary_700,
          }}
        >
          Signal Ticket: {MOCK_SIGNAL_MODEL.signalTicket} Signal:{" "}
          {MOCK_SIGNAL_MODEL.signalLogin}
        </Typography>
      )}

      {model === "CopyTicket" && (
        <Typography
          sx={{
            mb: 2,
            fontSize: "14px",
            fontWeight: 600,
            color: Colors.Secondary_700,
          }}
        >
          Copy Ticket: {MOCK_COPY_MODEL.copyTicket} Follower:{" "}
          {MOCK_COPY_MODEL.follower} SignalTicket: {MOCK_COPY_MODEL.signalTicket}{" "}
          Signal: {MOCK_COPY_MODEL.signalLogin}
        </Typography>
      )}

      {model === "SignalAccount" && (
        <Typography
          sx={{
            mb: 2,
            fontSize: "14px",
            fontWeight: 600,
            color: Colors.Secondary_700,
          }}
        >
          Signal: '{MOCK_SIGNAL_ACCOUNT_MODEL.signalLogin}'
        </Typography>
      )}

      {model === "FollowerAccount" && (
        <Typography
          sx={{
            mb: 2,
            fontSize: "14px",
            fontWeight: 600,
            color: Colors.Secondary_700,
          }}
        >
          Follower: '{MOCK_FOLLOWER_ACCOUNT_MODEL.followerLogin}' Signal: '
          {MOCK_FOLLOWER_ACCOUNT_MODEL.header.signal}'
        </Typography>
      )}

      {/* Main content */}
      <Box style={{ width: "100%", marginTop: "0px" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <CircularProgress color="primary" />
          </Box>
        ) : model === "SignalTicket" ? (
          <>
            {/* Card principal of SignalTicket */}
            <Box
              sx={{
                mb: 3,
                p: 2,
                borderRadius: "12px",
                border: "1px solid #E2E8F0",
                bgcolor: "#FFFFFF",
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(8, minmax(0, 1fr)) repeat(7, minmax(0, 1fr))",
                    columnGap: 2,
                    rowGap: 1,
                    flex: 1,
                    mr: 2,
                  }}
                >
                  <Typography sx={{ fontSize: "12px", color: Colors.Secondary_600 }}>
                    Signal: {signalHeader?.signal}
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: Colors.Secondary_600 }}>
                    Type: {signalHeader?.type}
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: Colors.Secondary_600 }}>
                    Markup: {signalHeader?.markup}
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: Colors.Secondary_600 }}>
                    Ticket: {signalHeader?.ticket}
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: Colors.Secondary_600 }}>
                    Type: {signalHeader?.typeOrder}
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: Colors.Secondary_600 }}>
                    Volume: {signalHeader?.volume}
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: Colors.Secondary_600 }}>
                    Symbol: {signalHeader?.symbol}
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: Colors.Secondary_600 }}>
                    Open time: {signalHeader?.openTime}
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: Colors.Secondary_600 }}>
                    Open Price: {signalHeader?.openPrice}
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: Colors.Secondary_600 }}>
                    Close time: {signalHeader?.closeTime}
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: Colors.Secondary_600 }}>
                    Close Price: {signalHeader?.closePrice}
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: Colors.Secondary_600 }}>
                    Commission: {signalHeader?.commission}
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: Colors.Secondary_600 }}>
                    Swap: {signalHeader?.swap}
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: Colors.Secondary_600 }}>
                    Profit: {signalHeader?.profit}
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: Colors.Secondary_600 }}>
                    Currency: {signalHeader?.currency}
                  </Typography>
                </Box>

                {/* Button Summary */}
                <Button3px
                  label={"Summary"}
                  color={Colors.Warning_50}
                  backgroundColor={Colors.Error_500}
                  type="button"
                  HandleOnClick={() => {
                    // In production, you would fetch the summary data for that specific SignalTicket from the backend instead of using MOCK data
                    setSummaryData(MOCK_SUMMARY_DATA);
                    setSummaryOpen(true);
                  }}
                  height="40px"
                >
                  Summary
                </Button3px>
              </Box>
            </Box>

            {/* Table of details (SignalTicket) */}
            <Box style={{ height: 500, width: "100%" }}>
              {signalDetails.length === 0 ? (
                <Box
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    color: Colors.Secondary_400,
                    fontSize: "14px",
                  }}
                >
                  <IconComponent icon="info" size="32px" />
                  <Typography sx={{ mt: 1 }}>
                    No results yet. Set your filters to find matching requests.
                  </Typography>
                </Box>
              ) : (
                <TableFrontPagination
                  rows={signalDetails}
                  columns={SIGNAL_DETAIL_COLUMNS}
                />
              )}
            </Box>
          </>
        ) : model === "CopyTicket" ? (
          <>
            {/* Card principal of CopyTicket */}
            <Box
              sx={{
                mb: 3,
                p: 2,
                borderRadius: "12px",
                border: "1px solid #E2E8F0",
                bgcolor: "#FFFFFF",
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(8, minmax(0, 1fr)) repeat(7, minmax(0, 1fr))",
                    columnGap: 2,
                    rowGap: 1,
                    flex: 1,
                    mr: 2,
                  }}
                >
                  <Typography sx={{ fontSize: "12px", color: Colors.Secondary_600 }}>
                    Copy Ticket: {copyHeader?.copyTicket}
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: Colors.Secondary_600 }}>
                    Follower: {copyHeader?.follower}
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: Colors.Secondary_600 }}>
                    SignalTicket: {copyHeader?.signalTicket}
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: Colors.Secondary_600 }}>
                    Signal: {copyHeader?.signal}
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: Colors.Secondary_600 }}>
                    Type: {copyHeader?.typeOrder}
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: Colors.Secondary_600 }}>
                    Volume: {copyHeader?.volume}
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: Colors.Secondary_600 }}>
                    Symbol: {copyHeader?.symbol}
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: Colors.Secondary_600 }}>
                    Open time: {copyHeader?.openTime}
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: Colors.Secondary_600 }}>
                    Open Price: {copyHeader?.openPrice}
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: Colors.Secondary_600 }}>
                    Close time: {copyHeader?.closeTime}
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: Colors.Secondary_600 }}>
                    Close Price: {copyHeader?.closePrice}
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: Colors.Secondary_600 }}>
                    Commission: {copyHeader?.commission}
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: Colors.Secondary_600 }}>
                    Swap: {copyHeader?.swap}
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: Colors.Secondary_600 }}>
                    Profit: {copyHeader?.profit}
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: Colors.Secondary_600 }}>
                    Currency: {copyHeader?.currency}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Table of details (CopyTicket) */}
            <Box style={{ height: 500, width: "100%" }}>
              {copyDetails.length === 0 ? (
                <Box
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    color: Colors.Secondary_400,
                    fontSize: "14px",
                  }}
                >
                  <IconComponent icon="info" size="32px" />
                  <Typography sx={{ mt: 1 }}>
                    No results yet. Set your filters to find matching requests.
                  </Typography>
                </Box>
              ) : (
                <TableFrontPagination
                  rows={copyDetails}
                  columns={COPY_DETAIL_COLUMNS}
                />
              )}
            </Box>
          </>
        ) : model === "SignalAccount" ? (
          <>
            {/* Card principal SignalAccount */}
            <Box
              sx={{
                mb: 3,
                p: 2,
                borderRadius: "12px",
                border: "1px solid #E2E8F0",
                bgcolor: "#FFFFFF",
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns:
                    "repeat(7, minmax(0, 1fr)) repeat(6, minmax(0, 1fr))",
                  columnGap: 2,
                  rowGap: 1,
                }}
              >
                <Typography sx={{ fontSize: "12px", color: Colors.Secondary_600 }}>
                  Signal: {signalAccountHeader?.signalLogin}
                </Typography>
                <Typography sx={{ fontSize: "12px", color: Colors.Secondary_600 }}>
                  Strategy: {signalAccountHeader?.strategy}
                </Typography>
                <Typography sx={{ fontSize: "12px", color: Colors.Secondary_600 }}>
                  Type: {signalAccountHeader?.type}
                </Typography>
                <Typography sx={{ fontSize: "12px", color: Colors.Secondary_600 }}>
                  Market: {signalAccountHeader?.market}
                </Typography>
                <Typography sx={{ fontSize: "12px", color: Colors.Secondary_600 }}>
                  Markup: {signalAccountHeader?.markup}
                </Typography>
                <Typography sx={{ fontSize: "12px", color: Colors.Secondary_600 }}>
                  Margin: {signalAccountHeader?.margin}
                </Typography>
                <Typography sx={{ fontSize: "12px", color: Colors.Secondary_600 }}>
                  Free Margin: {signalAccountHeader?.freeMargin}
                </Typography>
                <Typography sx={{ fontSize: "12px", color: Colors.Secondary_600 }}>
                  Balance: {signalAccountHeader?.balance}
                </Typography>
                <Typography sx={{ fontSize: "12px", color: Colors.Secondary_600 }}>
                  Credit: {signalAccountHeader?.credit}
                </Typography>
                <Typography sx={{ fontSize: "12px", color: Colors.Secondary_600 }}>
                  Equity: {signalAccountHeader?.equity}
                </Typography>
                <Typography sx={{ fontSize: "12px", color: Colors.Secondary_600 }}>
                  P&amp;L: {signalAccountHeader?.pnl}
                </Typography>
                <Typography sx={{ fontSize: "12px", color: Colors.Secondary_600 }}>
                  Currency: {signalAccountHeader?.currency}
                </Typography>
                <Typography sx={{ fontSize: "12px", color: Colors.Secondary_600 }}>
                  Group: {signalAccountHeader?.group}
                </Typography>
              </Box>
            </Box>

            {/* Table of details (SignalAccount) */}
            <Box style={{ height: 500, width: "100%" }}>
              {signalAccountDetails.length === 0 ? (
                <Box
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    color: Colors.Secondary_400,
                    fontSize: "14px",
                  }}
                >
                  <IconComponent icon="info" size="32px" />
                  <Typography sx={{ mt: 1 }}>
                    No results yet. Set your filters to find matching requests.
                  </Typography>
                </Box>
              ) : (
                <TableFrontPagination
                  rows={signalAccountDetails}
                  columns={SIGNAL_ACCOUNT_DETAIL_COLUMNS}
                />
              )}
            </Box>
          </>
        ) : model === "FollowerAccount" ? (
          <>
            {/* Card principal FollowerAccount */}
            <Box
              sx={{
                mb: 3,
                p: 2,
                borderRadius: "12px",
                border: "1px solid #E2E8F0",
                bgcolor: "#FFFFFF",
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns:
                    "repeat(7, minmax(0, 1fr)) repeat(6, minmax(0, 1fr))",
                  columnGap: 2,
                  rowGap: 1,
                }}
              >
                <Typography sx={{ fontSize: "12px", color: Colors.Secondary_600 }}>
                  Follower: {followerAccountHeader?.followerLogin}
                </Typography>
                <Typography sx={{ fontSize: "12px", color: Colors.Secondary_600 }}>
                  Signal: {followerAccountHeader?.signal}
                </Typography>
                <Typography sx={{ fontSize: "12px", color: Colors.Secondary_600 }}>
                  Type: {followerAccountHeader?.type}
                </Typography>
                <Typography sx={{ fontSize: "12px", color: Colors.Secondary_600 }}>
                  Market: {followerAccountHeader?.market}
                </Typography>
                <Typography sx={{ fontSize: "12px", color: Colors.Secondary_600 }}>
                  Markup: {followerAccountHeader?.markup}
                </Typography>
                <Typography sx={{ fontSize: "12px", color: Colors.Secondary_600 }}>
                  Margin: {followerAccountHeader?.margin}
                </Typography>
                <Typography sx={{ fontSize: "12px", color: Colors.Secondary_600 }}>
                  Free Margin: {followerAccountHeader?.freeMargin}
                </Typography>
                <Typography sx={{ fontSize: "12px", color: Colors.Secondary_600 }}>
                  Balance: {followerAccountHeader?.balance}
                </Typography>
                <Typography sx={{ fontSize: "12px", color: Colors.Secondary_600 }}>
                  Credit: {followerAccountHeader?.credit}
                </Typography>
                <Typography sx={{ fontSize: "12px", color: Colors.Secondary_600 }}>
                  Equity: {followerAccountHeader?.equity}
                </Typography>
                <Typography sx={{ fontSize: "12px", color: Colors.Secondary_600 }}>
                  P&amp;L: {followerAccountHeader?.pnl}
                </Typography>
                <Typography sx={{ fontSize: "12px", color: Colors.Secondary_600 }}>
                  Currency: {followerAccountHeader?.currency}
                </Typography>
                <Typography sx={{ fontSize: "12px", color: Colors.Secondary_600 }}>
                  Group: {followerAccountHeader?.group}
                </Typography>
              </Box>
            </Box>

            {/* Table of details (FollowerAccount) */}
            <Box style={{ height: 500, width: "100%" }}>
              {followerAccountDetails.length === 0 ? (
                <Box
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    color: Colors.Secondary_400,
                    fontSize: "14px",
                  }}
                >
                  <IconComponent icon="info" size="32px" />
                  <Typography sx={{ mt: 1 }}>
                    No results yet. Set your filters to find matching requests.
                  </Typography>
                </Box>
              ) : (
                <TableFrontPagination
                  rows={followerAccountDetails}
                  columns={FOLLOWER_ACCOUNT_DETAIL_COLUMNS}
                />
              )}
            </Box>
          </>
        ) : (
          // Basic view (copies table)
          <Box style={{ height: 650, width: "100%" }}>
            {rows.length === 0 ? (
              <Box
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  color: Colors.Secondary_400,
                  fontSize: "14px",
                }}
              >
                <IconComponent icon="info" size="32px" />
                <Typography sx={{ mt: 1 }}>
                  No results yet. Set your filters to find matching requests.
                </Typography>
              </Box>
            ) : (
              <TableFrontPagination rows={rows} columns={COPIES_COLUMNS} />
            )}
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default CopiesScreen;